import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Mail, Phone, ShieldCheck, UserRound } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import AnimatoForm from "@/components/AnimatoForm";
import RiepilogoIscrizione from "@/components/RiepilogoIscrizione";
import { AppLogo, Field } from "@/components/Shared";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const RELAZIONI = [
  ["madre", "Madre"],
  ["padre", "Padre"],
  ["tutore", "Tutore"],
  ["altro_esercente", "Altro esercente"],
];

export default function GenitoreRegistrazione() {
  const { user } = useAuth();
  const accountMode = user?.role === "genitore";
  const [stato, setStato] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConferma, setPasswordConferma] = useState("");
  const [accountNome, setAccountNome] = useState("");
  const [accountCognome, setAccountCognome] = useState("");
  const [accountTelefono, setAccountTelefono] = useState("");
  const [accountRelazione, setAccountRelazione] = useState("altro_esercente");
  const [saving, setSaving] = useState(false);
  const [inviata, setInviata] = useState(false);
  const [inviatoTipo, setInviatoTipo] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [riepilogo, setRiepilogo] = useState(null);
  const [initialForm, setInitialForm] = useState(null);

  useEffect(() => {
    // L'account adulto non dipende dall'apertura delle iscrizioni Animati.
    if (!accountMode) return undefined;
    api.get("/public/iscrizioni/stato")
      .then((r) => setStato(r.data))
      .catch(() => setStato({ aperte: false, motivo: "Servizio non disponibile" }));
    return undefined;
  }, [accountMode]);

  useEffect(() => {
    if (!accountMode) return;
    api.get("/genitore/me").then(({ data }) => {
      const accountGenitore = data?.utente || {};
      const relazione = accountGenitore.relazione || data?.animati?.[0]?.relazione || "altro_esercente";
      setInitialForm({
        compilante: {
          nome: accountGenitore.nome || "",
          cognome: accountGenitore.cognome || "",
          telefono: accountGenitore.telefono || "",
          relazione,
        },
      });
    }).catch(() => setInitialForm({ compilante: { nome: "", cognome: "", telefono: "", relazione: "altro_esercente" } }));
  }, [accountMode]);

  const invia = async (iscrizione) => {
    setSaving(true);
    try {
      const { data } = await api.post("/genitore/iscrizioni", iscrizione);
      setSuccessMessage(data?.messaggio || "Grazie per aver inviato la richiesta! Ti aspettiamo in Parrocchia per confermare l'iscrizione. A presto!");
      setRiepilogo(data?.riepilogo || null);
      setInviatoTipo("figlio");
      setInviata(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const creaAccount = async (event) => {
    event.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return toast.error("Inserisci un indirizzo email valido");
    if (password.length < 8) return toast.error("La password deve contenere almeno 8 caratteri");
    if (password !== passwordConferma) return toast.error("Le password non coincidono");
    if (!accountNome.trim() || !accountCognome.trim() || !accountTelefono.trim()) return toast.error("Completa i dati personali del genitore");
    setSaving(true);
    try {
      const { data } = await api.post("/public/genitori/registrazione", {
        email: email.trim().toLowerCase(),
        password,
        password_conferma: passwordConferma,
        genitore: {
          nome: accountNome.trim(),
          cognome: accountCognome.trim(),
          telefono: accountTelefono.trim(),
          relazione: accountRelazione,
        },
      });
      setSuccessMessage(data?.messaggio || "Account Genitore creato. Ora puoi accedere e aggiungere il primo Animato.");
      setInviatoTipo("account");
      setInviata(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-dvh bg-[#FFFDF9] py-8 px-4">
      <div className="max-w-xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center mb-6">
          <AppLogo size={64} className="mb-3 drop-shadow-lg" />
          <h1 className="font-display text-2xl font-extrabold text-slate-900 text-center">{accountMode ? "Aggiungi un figlio" : "Crea account Genitore"}</h1>
          <p className="text-sm font-semibold text-slate-500 text-center">{accountMode ? "Invia una nuova richiesta di iscrizione" : "Crea il tuo accesso e gestisci la tua famiglia dall'app"}</p>
        </motion.div>

        {inviata ? (
          <>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="card-app p-8 text-center" data-testid="genitore-richiesta-inviata">
              <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
              <h2 className="font-display text-xl font-extrabold text-slate-900 mb-2">{inviatoTipo === "account" ? "Account creato" : "Richiesta inviata"}</h2>
              <p className="text-sm text-slate-500 font-semibold">{successMessage}</p>
              <Link to={accountMode ? "/genitore" : "/login"} className="btn-primary inline-flex mt-6">{accountMode ? "Torna alla mia area" : "Vai al login"}</Link>
            </motion.div>
            {riepilogo && <div className="card-app mt-4 print:mt-0 print:shadow-none"><RiepilogoIscrizione dati={riepilogo} /></div>}
          </>
        ) : accountMode && !stato ? (
          <p className="text-center text-slate-400 font-semibold py-8">Caricamento...</p>
        ) : accountMode && !stato.aperte ? (
          <div className="card-app p-8 text-center" data-testid="genitore-iscrizioni-chiuse">
            <h2 className="font-display text-xl font-extrabold text-slate-900 mb-2">Iscrizioni chiuse</h2>
            <p className="text-sm text-slate-500 font-semibold">{stato.motivo}</p>
            <Link to="/genitore" className="btn-secondary inline-flex mt-5">Torna alla mia area</Link>
          </div>
        ) : accountMode && !initialForm ? (
          <p className="text-center text-slate-400 font-semibold py-8">Caricamento dati account...</p>
        ) : !accountMode ? (
          <form onSubmit={creaAccount} className="card-app p-6" data-testid="genitore-account-registration-form">
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4 mb-5 flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
              <div><p className="font-display font-extrabold text-slate-900">Il tuo account, poi i tuoi figli</p><p className="text-xs text-slate-600 font-semibold mt-1">L'account Genitore viene creato subito. L'iscrizione di ogni Animato sarà una richiesta separata verificata dalla Segreteria.</p></div>
            </div>
            <div className="flex items-center gap-3 mb-4"><div className="h-10 w-10 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center"><UserRound className="h-5 w-5" /></div><div><p className="font-display font-extrabold text-slate-900">Dati dell'account</p><p className="text-xs text-slate-500 font-semibold">Usali per accedere all'area famiglia.</p></div></div>
            <Field label="Email"><div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><input className="input-app !pl-10" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@esempio.it" autoCapitalize="none" autoCorrect="off" data-testid="genitore-account-email" /></div></Field>
            <div className="grid sm:grid-cols-2 gap-3"><Field label="Password"><input className="input-app" type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="genitore-account-password" /></Field><Field label="Conferma password"><input className="input-app" type="password" minLength={8} required value={passwordConferma} onChange={(e) => setPasswordConferma(e.target.value)} data-testid="genitore-account-password-confirm" /></Field></div>
            <div className="border-t border-slate-100 pt-5 mt-2"><div className="flex items-center gap-3 mb-4"><div className="h-10 w-10 rounded-2xl bg-sky-100 text-sky-600 flex items-center justify-center"><UserRound className="h-5 w-5" /></div><div><p className="font-display font-extrabold text-slate-900">Dati personali</p><p className="text-xs text-slate-500 font-semibold">Verranno riutilizzati quando aggiungerai un figlio.</p></div></div><div className="grid sm:grid-cols-2 gap-3"><Field label="Nome"><input className="input-app" required value={accountNome} onChange={(e) => setAccountNome(e.target.value)} data-testid="genitore-account-nome" /></Field><Field label="Cognome"><input className="input-app" required value={accountCognome} onChange={(e) => setAccountCognome(e.target.value)} data-testid="genitore-account-cognome" /></Field></div><div className="grid sm:grid-cols-2 gap-3"><Field label="In qualità di"><select className="input-app" required value={accountRelazione} onChange={(e) => setAccountRelazione(e.target.value)}>{RELAZIONI.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></Field><Field label="Telefono"><div className="relative"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><input className="input-app !pl-10" type="tel" required value={accountTelefono} onChange={(e) => setAccountTelefono(e.target.value)} data-testid="genitore-account-telefono" /></div></Field></div></div>
            <button className="btn-primary w-full mt-5" disabled={saving} data-testid="genitore-account-submit">{saving ? "Creazione account..." : "Crea account Genitore"}</button>
            <Link to="/login" className="mt-4 block text-center text-xs font-extrabold text-slate-500 hover:text-slate-800">Hai già un account? Accedi</Link>
          </form>
        ) : (
          <>
            <div className="card-app p-5 mb-4" data-testid="genitore-account-attivo"><p className="font-display font-extrabold text-slate-900">Aggiungi un Animato</p><p className="text-xs text-slate-500 font-semibold mt-1">La richiesta verrà collegata al tuo account e resterà in attesa della verifica della Segreteria.</p></div>
            <div className="card-app p-6">
              <AnimatoForm isPublic wizard account={{}} accountMode initial={initialForm} onSubmit={invia} saving={saving} submitLabel={saving ? "Invio richiesta..." : "Invia richiesta"} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
